// src/pages/UserManagement.tsx
import { useState, useEffect, useMemo, type ReactNode } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { getUsers, updateUserStatus, createUser } from "@/api/user";
import { User } from '@/interfaces/user';
import { Modal } from "@/components/ui/modal";
import { Search, ArrowUpDown, ArrowUp, ArrowDown, Filter } from "lucide-react";
import { cn } from "@/lib/utils";

type SortColumn =
  | "DisplayName"
  | "Email"
  | "Role"
  | "Status"
  | "CreatedAt"
  | "UpdatedAt";

type SortDirection = "asc" | "desc";
type StatusFilterValue = "Active" | "Inactive";

const STATUS_FILTER_OPTIONS: StatusFilterValue[] = ["Active", "Inactive"];

const parseDateValue = (iso?: string) => {
  if (!iso) return 0;
  const hasTZ = /Z|[+\-]\d{2}:\d{2}$/.test(iso);
  const ds = hasTZ ? iso : `${iso}Z`;
  const d = new Date(ds);
  return isNaN(d.getTime()) ? 0 : d.getTime();
};

const getStatusLabel = (isActive: boolean): StatusFilterValue =>
  isActive ? "Active" : "Inactive";

const UserManagement = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<StatusFilterValue[]>([]);
  const [sortColumn, setSortColumn] = useState<SortColumn>("DisplayName");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [isLoading, setIsLoading] = useState(false);
  const [modalData, setModalData] = useState<{ email: string; currentStatus: boolean } | null>(null);
  const [confirmationMessage, setConfirmationMessage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false); 
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  const [newUser, setNewUser] = useState({ DisplayName: "", Email: "", Role: "User" });
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [visiblePasswords, setVisiblePasswords] = useState<{ [key: string]: boolean }>({});
  const [copiedUserId, setCopiedUserId] = useState<string | null>(null);
  const [apiResponseData, setApiResponseData] = useState<any[]>([]);
  const [isApiResponseLoading, setIsApiResponseLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async () => {
    if (!modalData) return;

    const { email, currentStatus } = modalData;

    try {
      setIsProcessing(true); 
      const response = await updateUserStatus(email, !currentStatus); 
      setConfirmationMessage(`${email} has been set to ${!currentStatus ? "Active" : "Inactive"}. ${response.message}`);
      fetchUsers(); // Refresh the user list
    } catch (error) {
      setConfirmationMessage("Failed to update user status. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOpenCreateUserModal = () => {
    setNewUser({ DisplayName: "", Email: "", Role: "User" });
    setApiResponseData([]);
    setApiError(null);
    setIsCreateUserModalOpen(true);
  };

  const handleCloseCreateUserModal = () => {
    setIsCreateUserModalOpen(false);
    setNewUser({ DisplayName: "", Email: "", Role: "User" });
    setApiResponseData([]);
    setApiError(null);
    setIsCreatingUser(false);
    setIsApiResponseLoading(false);
    fetchUsers(); 
  };

  const handleCreateUser = async () => {
    try {
      setIsCreatingUser(true);
      setIsApiResponseLoading(true);
      const response = await createUser(newUser);

      // Check for error response
      if (response.ErrorNumber && response.ErrorMessage) {
        setApiError(response.ErrorMessage);
        setApiResponseData([]);
      } else {
        setApiError(null);
        const processedResponse = [
          { Key: "Username", Value: response.Username },
          { Key: "Password", Value: response.Password },
          { Key: "Email", Value: response.Email },
          { Key: "Role", Value: response.Role },
          { Key: "Created", Value: response.Created ? new Date(response.Created).toLocaleString() : "" },
        ];
        setApiResponseData(processedResponse);
      }
    } catch (error) {
      setApiError("Failed to create user.");
      setApiResponseData([]);
      console.error("Failed to create user:", error);
    } finally {
      setIsCreatingUser(false);
      setIsApiResponseLoading(false);
    }
  };

  const togglePasswordVisibility = (userId: string) => {
    setVisiblePasswords((prev) => ({
      ...prev,
      [userId]: true, 
    }));

    // Automatically hide the password after 10 seconds
    setTimeout(() => {
      setVisiblePasswords((prev) => ({
        ...prev,
        [userId]: false, 
      }));
    }, 10000);
  };

  const copyToClipboard = (password: string, userId: string) => {
    navigator.clipboard.writeText(password)
      .then(() => {
        setCopiedUserId(userId); 
        setTimeout(() => setCopiedUserId(null), 2000); 
      })
      .catch((error) => {
        console.error("Failed to copy password:", error);
      });
  };

  const closeModal = () => {
    setModalData(null);
    setConfirmationMessage(null);
    setIsProcessing(false); 
  };

  // Helper: parse ISO-like datetime to local date string
  const parseToLocalDate = (iso?: string) => {
    if (!iso) return "";
    const hasTZ = /Z|[+\-]\d{2}:\d{2}$/.test(iso);
    const ds = hasTZ ? iso : `${iso}Z`;
    const d = new Date(ds);
    return isNaN(d.getTime()) ? iso : d.toLocaleDateString();
  };

  const availableRoles = useMemo(() => {
    return [...new Set(users.map((user) => user.Role).filter(Boolean))].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );
  }, [users]);

  useEffect(() => {
    setSelectedRoles((prev) => {
      const next = prev.filter((role) => availableRoles.includes(role));
      return next.length === prev.length ? prev : next;
    });
  }, [availableRoles]);

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const filtered = users.filter((user) => {
      const statusLabel = getStatusLabel(user.IsActive);
      const matchesSearch =
        !query ||
        user.DisplayName?.toLowerCase().includes(query) ||
        user.Email?.toLowerCase().includes(query) ||
        user.Role?.toLowerCase().includes(query) ||
        statusLabel.toLowerCase().includes(query);
      const matchesRole =
        selectedRoles.length === 0 || selectedRoles.includes(user.Role);
      const matchesStatus =
        selectedStatuses.length === 0 || selectedStatuses.includes(statusLabel);

      return matchesSearch && matchesRole && matchesStatus;
    });

    const direction = sortDirection === "asc" ? 1 : -1;

    filtered.sort((a, b) => {
      switch (sortColumn) {
        case "DisplayName":
          return (
            a.DisplayName.localeCompare(b.DisplayName, undefined, {
              sensitivity: "base",
              numeric: true,
            }) * direction
          );
        case "Email":
          return (
            a.Email.localeCompare(b.Email, undefined, {
              sensitivity: "base",
              numeric: true,
            }) * direction
          );
        case "Role":
          return (
            a.Role.localeCompare(b.Role, undefined, { sensitivity: "base" }) *
            direction
          );
        case "Status": {
          const statusA = a.IsActive ? 1 : 0;
          const statusB = b.IsActive ? 1 : 0;
          return (statusA - statusB) * direction;
        }
        case "CreatedAt":
          return (parseDateValue(a.CreatedAt) - parseDateValue(b.CreatedAt)) * direction;
        case "UpdatedAt":
          return (parseDateValue(a.UpdatedAt) - parseDateValue(b.UpdatedAt)) * direction;
        default: {
          const _exhaustive: never = sortColumn;
          return _exhaustive;
        }
      }
    });

    return filtered;
  }, [users, searchQuery, selectedRoles, selectedStatuses, sortColumn, sortDirection]);

  const hasColumnFilters = selectedRoles.length > 0 || selectedStatuses.length > 0;

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortColumn(column);
    setSortDirection("asc");
  };

  const toggleRoleFilter = (role: string) => {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((item) => item !== role) : [...prev, role]
    );
  };

  const toggleStatusFilter = (status: StatusFilterValue) => {
    setSelectedStatuses((prev) =>
      prev.includes(status) ? prev.filter((item) => item !== status) : [...prev, status]
    );
  };

  const SortIcon = ({ column }: { column: SortColumn }) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />;
    }
    return sortDirection === "asc" ? (
      <ArrowUp className="h-3.5 w-3.5" />
    ) : (
      <ArrowDown className="h-3.5 w-3.5" />
    );
  };

  const ColumnFilter = ({
    label,
    activeCount,
    onSelectAll,
    onClear,
    children,
  }: {
    label: string;
    activeCount: number;
    onSelectAll: () => void;
    onClear: () => void;
    children: ReactNode;
  }) => (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex h-6 w-6 items-center justify-center rounded-md transition-colors hover:bg-muted",
            activeCount > 0
              ? "text-primary"
              : "text-muted-foreground hover:text-foreground"
          )}
          aria-label={`Filter ${label}`}
          onClick={(event) => event.stopPropagation()}
        >
          <Filter className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-56 p-3"
        align="start"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h4 className="text-sm font-medium">Filter {label}</h4>
            <div className="flex gap-1">
              <Button type="button" size="sm" variant="ghost" className="h-7 px-2" onClick={onSelectAll}>
                All
              </Button>
              <Button type="button" size="sm" variant="ghost" className="h-7 px-2" onClick={onClear}>
                Clear
              </Button>
            </div>
          </div>
          <div className="max-h-48 space-y-2 overflow-y-auto">{children}</div>
        </div>
      </PopoverContent>
    </Popover>
  );

  const SortableHead = ({
    column,
    children,
    filter,
  }: {
    column: SortColumn;
    children: ReactNode;
    filter?: ReactNode;
  }) => (
    <TableHead>
      <div className="inline-flex items-center gap-1">
        <button
          type="button"
          onClick={() => handleSort(column)}
          className={cn(
            "inline-flex items-center gap-1.5 font-medium transition-colors hover:text-foreground",
            sortColumn === column ? "text-foreground" : "text-muted-foreground"
          )}
        >
          {children}
          <SortIcon column={column} />
        </button>
        {filter}
      </div>
    </TableHead>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold">User Management</h1>
        <button
          className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
          onClick={handleOpenCreateUserModal}
        >
          Create New User
        </button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name, email, role, or status..."
          className="pl-10"
          aria-label="Search users"
        />
      </div>

      <div className="rounded-lg shadow-sm overflow-hidden bg-card text-card-foreground">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <SortableHead column="DisplayName">Display Name</SortableHead>
              <SortableHead column="Email">Email</SortableHead>
              <SortableHead
                column="Role"
                filter={
                  <ColumnFilter
                    label="Role"
                    activeCount={selectedRoles.length}
                    onSelectAll={() => setSelectedRoles([...availableRoles])}
                    onClear={() => setSelectedRoles([])}
                  >
                    {availableRoles.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No roles available</p>
                    ) : (
                      availableRoles.map((role) => (
                        <div key={role} className="flex items-center space-x-2">
                          <Checkbox
                            id={`role-filter-${role}`}
                            checked={selectedRoles.includes(role)}
                            onCheckedChange={() => toggleRoleFilter(role)}
                          />
                          <label
                            htmlFor={`role-filter-${role}`}
                            className="cursor-pointer text-sm"
                          >
                            {role}
                          </label>
                        </div>
                      ))
                    )}
                  </ColumnFilter>
                }
              >
                Role
              </SortableHead>
              <SortableHead
                column="Status"
                filter={
                  <ColumnFilter
                    label="Status"
                    activeCount={selectedStatuses.length}
                    onSelectAll={() => setSelectedStatuses([...STATUS_FILTER_OPTIONS])}
                    onClear={() => setSelectedStatuses([])}
                  >
                    {STATUS_FILTER_OPTIONS.map((status) => (
                      <div key={status} className="flex items-center space-x-2">
                        <Checkbox
                          id={`status-filter-${status}`}
                          checked={selectedStatuses.includes(status)}
                          onCheckedChange={() => toggleStatusFilter(status)}
                        />
                        <label
                          htmlFor={`status-filter-${status}`}
                          className="cursor-pointer text-sm"
                        >
                          {status}
                        </label>
                      </div>
                    ))}
                  </ColumnFilter>
                }
              >
                Status
              </SortableHead>
              <TableHead>Password</TableHead>
              <SortableHead column="CreatedAt">Created At</SortableHead>
              <SortableHead column="UpdatedAt">Updated At</SortableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.UserID} className="bg-card text-card-foreground">
                <TableCell className="font-medium">{user.DisplayName}</TableCell>
                <TableCell>{user.Email}</TableCell>
                <TableCell>{user.Role}</TableCell>
                <TableCell className="flex items-center space-x-2">
                  <Badge
                    variant={user.IsActive ? "default" : "secondary"}
                    className={user.IsActive
                      ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                      : "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300"}
                  >
                    {user.IsActive ? "Active" : "Inactive"}
                  </Badge>
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={user.IsActive}
                      onChange={() => setModalData({ email: user.Email, currentStatus: user.IsActive })}
                    />
                    <div
                      className={`w-10 h-6 rounded-full transition-colors ${
                        user.IsActive ? "bg-green-500" : "bg-gray-300"
                      }`}
                    >
                      <div
                        className={`w-4 h-4 bg-white rounded-full transform transition-transform ${
                          user.IsActive ? "translate-x-4" : ""
                        }`}
                      ></div>
                    </div>
                  </label>
                </TableCell>
                <TableCell>
                  <div className="flex items-center space-x-2">
                    <span>
                      {visiblePasswords[user.UserID] ? user.Password : "********"} {/* Show or hide password */}
                    </span>
                    <button
                      className="text-blue-500 hover:text-blue-700"
                      onClick={() => togglePasswordVisibility(user.UserID)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-.274.857-.68 1.654-1.196 2.364M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    </button>
                    {visiblePasswords[user.UserID] && (
                      <div className="relative group">
                        <button
                          className="text-blue-500 hover:text-blue-700 flex items-center"
                          onClick={() => copyToClipboard(user.Password, user.UserID)}
                        >
                          {copiedUserId === user.UserID ? (
                            <span className="text-green-500 text-sm">Copied</span>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M16 8H8m8 4H8m8 4H8m8-12H8a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V6a2 2 0 00-2-2z"
                              />
                            </svg>
                          )}
                        </button>
                        <span className="absolute bottom-full mb-1 left-1/2 transform -translate-x-1/2 bg-gray-700 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          Copy
                        </span>
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>{parseToLocalDate(user.CreatedAt)}</TableCell>
                <TableCell>{parseToLocalDate(user.UpdatedAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {filteredUsers.length === 0 && !isLoading && (
          <div className="text-center py-8 text-muted-foreground">
            {searchQuery.trim() || hasColumnFilters
              ? "No users match your search or filters"
              : "No users found"}
          </div>
        )}

        {isLoading && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          </div>
        )}
      </div>

      {modalData && (
        <Modal
          title="Confirm Status Change"
          description={
            confirmationMessage
              ? confirmationMessage
              : `Are you sure you want to set this user as ${!modalData.currentStatus ? "Active" : "Inactive"}?`
          }
          onConfirm={confirmationMessage ? closeModal : handleStatusChange}
          onCancel={closeModal}
          isProcessing={isProcessing}
          showCloseButton={!!confirmationMessage} 
        />
      )}

      {isCreateUserModalOpen && (
        <Modal
          title={
            apiError
              ? "User Creation Failed"
              : !apiResponseData.length
              ? "Create New User"
              : "User Created Successfully"
          }
          description={
            apiError
              ? ""
              : !apiResponseData.length
              ? "Fill in the details below to create a new user."
              : ""
          }
          onConfirm={
            (!apiResponseData.length && !apiError)
              ? handleCreateUser
              : handleCloseCreateUserModal 
          }
          onCancel={handleCloseCreateUserModal} 
          isProcessing={isCreatingUser}
          confirmButtonLabel={!apiResponseData.length && !apiError ? "Create User" : "Close"}
        >
          <div className="space-y-4">
            {apiError ? (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
                <strong className="font-bold">Error: </strong>
                <span className="block">{apiError}</span>
              </div>
            ) : !apiResponseData.length ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Display Name</label>
                  <input
                    type="text"
                    className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                    placeholder="Enter display name"
                    value={newUser.DisplayName}
                    onChange={(e) => setNewUser({ ...newUser, DisplayName: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                    placeholder="Enter email address"
                    value={newUser.Email}
                    onChange={(e) => setNewUser({ ...newUser, Email: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Role</label>
                  <select
                    className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                    value={newUser.Role}
                    onChange={(e) => setNewUser({ ...newUser, Role: e.target.value })}
                  >
                    <option value="User">User</option>
                    <option value="Admin">Admin</option>
                  </select>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                <Table>
                  <TableBody>
                    {apiResponseData.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{row.Key}</TableCell>
                        <TableCell>{row.Value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default UserManagement;